import React, { useCallback, useEffect, useMemo, useState } from "react";
import AuthService from "../../network/AuthService";
import ApiService from "../../network/ApiService";
import useTransferWindow from "../../hooks/useTransferWindow";
import styles from "./TransferList.module.css";

const field = (record, snakeCase, camelCase, fallback = "") =>
  record?.[snakeCase] ?? record?.[camelCase] ?? fallback;

const STATUS = {
  warten: { label: "Ausstehend", className: "pending" },
  angenommen: { label: "Angenommen", className: "accepted" },
  abgelehnt: { label: "Abgelehnt", className: "rejected" },
  abgelent: { label: "Abgelehnt", className: "rejected" },
  abgelaufen: { label: "Abgelaufen", className: "expired" },
};

function TransferImage({ src, alt, variant = "player" }) {
  const [failed, setFailed] = useState(!src);

  useEffect(() => setFailed(!src), [src]);

  if (failed) {
    return (
      <div className={`${styles.imageFallback} ${styles[variant]}`} aria-label={`${alt}: kein Bild`}>
        {variant === "player" ? "👤" : "⚽"}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${styles.image} ${styles[variant]}`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

const TransferList = () => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const authService = useMemo(() => new AuthService(), []);
  const apiService = useMemo(() => new ApiService(), []);
  const { isOpen, loading: windowLoading } = useTransferWindow();

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const teamID = authService.getTeamID();
      const authToken = authService.getAuthToken();

      if (!teamID || !authToken) {
        throw new Error("Die Team-Anmeldung ist nicht mehr gültig.");
      }

      const response = await apiService.get(`app/transfer/team/${teamID}`);
      setTransfers(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Error fetching transfers:", error);
      setTransfers([]);
      setErrorMessage(error.message || "Transfers konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [apiService, authService]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleRejectTransfer = async (transferID) => {
    try {
      await apiService.get(`app/transfer/reject/${transferID}`);
      await fetchTransfers();
    } catch (error) {
      console.error(`Error rejecting transfer ${transferID}:`, error);
      setErrorMessage(error.message || "Der Transfer konnte nicht storniert werden.");
    }
  };

  if (loading) return <div className={styles.loading}>Transfers werden geladen …</div>;

  return (
    <div className={styles.transfersList}>
      {!windowLoading && !isOpen && (
        <div className={styles.closedBanner} role="status">
          <span aria-hidden="true">🔒</span>
          <div>
            <strong>Transferfenster geschlossen</strong>
            <p>Neue Anfragen sind derzeit nicht möglich. Ihre bisherigen Transfers bleiben hier sichtbar.</p>
          </div>
        </div>
      )}

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      {transfers.length === 0 ? (
        <div className={styles.emptyState}>Noch keine Transfers vorhanden.</div>
      ) : (
        <div className={styles.transferGrid}>
          {transfers.map((transfer) => {
            const playerName = field(transfer, "player_name", "playerName", "Unbekannter Spieler");
            const playerImage = field(transfer, "player_image", "playerImage");
            const originName = field(transfer, "origin_name", "originName", "Bisheriges Team");
            const originImage = field(transfer, "origin_image", "originImage");
            const teamName = field(transfer, "team_name", "teamName", "Neues Team");
            const teamImage = field(transfer, "team_image", "teamImage");
            const statusKey = String(transfer.status || "warten").toLowerCase();
            const status = STATUS[statusKey] || { label: transfer.status || "Unbekannt", className: "expired" };
            const canCancel = statusKey === "warten";

            return (
              <article key={transfer.id} className={styles.transferCard}>
                <div className={styles.cardHeader}>
                  <span className={`${styles.status} ${styles[status.className]}`}>{status.label}</span>
                  <time dateTime={transfer.created || undefined}>
                    {transfer.created ? new Date(transfer.created).toLocaleDateString("de-AT") : "Kein Datum"}
                  </time>
                </div>

                <div className={styles.transferRoute}>
                  <div className={styles.teamBlock}>
                    <TransferImage src={originImage} alt={originName} variant="team" />
                    <span>{originName}</span>
                  </div>
                  <span className={styles.arrow} aria-label="wechselt zu">→</span>
                  <div className={styles.teamBlock}>
                    <TransferImage src={teamImage} alt={teamName} variant="team" />
                    <span>{teamName}</span>
                  </div>
                </div>

                <div className={styles.playerBlock}>
                  <TransferImage src={playerImage} alt={playerName} />
                  <div>
                    <span className={styles.eyebrow}>Spieler</span>
                    <h3>{playerName}</h3>
                  </div>
                </div>

                {canCancel && (
                  <button className={styles.rejectButton} onClick={() => handleRejectTransfer(transfer.id)}>
                    Transfer stornieren
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransferList;
